import { useEffect, useRef, useState } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";

/**
 * Hook qui vérifie et accepte automatiquement les invitations en attente
 * 
 * Fonctionnement:
 * 1. Détecte si un token d'invitation est présent dans l'URL
 * 2. Appelle l'edge function pour accepter l'invitation
 * 3. Gère le cas "déjà membre" comme un succès (idempotence)
 * 4. Refresh le profil pour synchroniser organization_id
 * 5. Redirige vers la page courante (ou /dashboard si venant de /auth)
 * 6. Nettoie TOUJOURS l'URL (évite les boucles)
 * 
 * Garde-fous:
 * - Ne s'exécute que si user connecté
 * - Ignore les routes /invite/* (signup, accept)
 * - Ne traite chaque token qu'une seule fois (lastTokenRef)
 * - Nettoie systématiquement l'URL (stabilité)
 * - Gère les erreurs de refresh gracieusement
 * 
 * @returns {Object} { checking: boolean } - État du traitement
 */
export function useInvitationCheck() {
  const { user, loading: authLoading, refreshProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [checking, setChecking] = useState(false);
  
  // ✅ Mémoriser le dernier token traité pour éviter les doubles exécutions
  // et permettre le traitement d'un nouveau token sans reload
  const lastTokenRef = useRef<string | null>(null);
  
  useEffect(() => {
    // ✅ Guard 1: Attendre la fin du chargement auth
    if (authLoading) return;
    
    // ✅ Guard 2: Pas d'utilisateur → rien à faire
    if (!user) return;
    
    // ✅ Guard 3: Ignorer les routes /invite/* (signup, accept)
    if (location.pathname.startsWith("/invite/")) {
      console.log("🛑 useInvitationCheck: Route /invite/* ignorée");
      return;
    }
    
    // ✅ Guard 4: Pas de token → rien à faire
    const token = searchParams.get("invite_token") || searchParams.get("token");
    if (!token) return;
    
    // ✅ Guard 5: Éviter de traiter le même token plusieurs fois
    // Permet aussi de traiter un nouveau token sans reload
    if (lastTokenRef.current === token) return;
    lastTokenRef.current = token;
    
    // ✅ Sauvegarder la route d'origine pour y retourner après acceptation
    const returnPath = location.pathname === "/auth" ? "/dashboard" : location.pathname;
    
    const acceptInvitation = async () => {
      setChecking(true);
      
      try {
        console.log("🔍 useInvitationCheck: Token détecté", token);
        
        const { data, error } = await supabase.functions.invoke(
          "accept-organization-invitation",
          { body: { token } }
        );
        
        // ✅ Vérifier data.code en priorité (plus robuste que message)
        const alreadyMember = 
          data?.code === "ALREADY_MEMBER" ||           // Priorité 1: code structuré
          (error as any)?.message?.includes("déjà membre") ||  // Fallback 1
          (error as any)?.message?.includes("ALREADY_MEMBER"); // Fallback 2
        
        if (error && !alreadyMember) {
          // Erreur réelle (pas "déjà membre")
          console.error("❌ useInvitationCheck: Erreur", error);
          throw error;
        }
        
        if (alreadyMember) {
          // ✅ Déjà membre → refresh quand même pour synchroniser organization_id
          // L'utilisateur peut être déjà membre mais profiles.organization_id peut pointer
          // vers une autre org (ex: org parasite créée avant le fix du trigger)
          console.log("✅ useInvitationCheck: Déjà membre (OK)");
          
          try {
            console.log("🔄 useInvitationCheck: Refresh du profil (déjà membre)");
            await refreshProfile();
            navigate(returnPath, { replace: true });
          } catch (refreshError) {
            console.error("❌ useInvitationCheck: Échec refresh profil (déjà membre)", refreshError);
            // Rediriger quand même
            navigate(returnPath, { replace: true });
          }
          
          return; // ✅ Sortir pour éviter le bloc "succès" ci-dessous
        }
        
        // ✅ Succès → toast informatif
        console.log("✅ useInvitationCheck: Invitation acceptée", data);
        toast({
          title: "Invitation acceptée",
          description: "Vous avez rejoint l'organisation avec succès !",
        });
        
        // ✅ CRITIQUE: Refresh du profil pour mettre à jour organization_id
        // L'edge function a mis à jour profiles.organization_id côté DB
        // On recharge le profil en mémoire pour synchroniser
        try {
          console.log("🔄 useInvitationCheck: Refresh du profil");
          await refreshProfile();
          
          // ✅ Rediriger vers la route d'origine (ou dashboard par défaut)
          console.log("🔄 useInvitationCheck: Redirection vers", returnPath);
          navigate(returnPath, { replace: true });
        } catch (refreshError) {
          // ❌ Échec du refresh (réseau, etc.)
          console.error("❌ useInvitationCheck: Échec refresh profil", refreshError);
          
          // ✅ Toast d'avertissement mais on redirige quand même
          // L'invitation a été acceptée côté serveur, c'est l'essentiel
          toast({
            title: "Invitation acceptée",
            description: "Vous avez rejoint l'organisation. Rechargez la page si nécessaire.",
            variant: "default",
          });
          
          // ✅ Rediriger quand même (le profil se rechargera au prochain mount)
          navigate(returnPath, { replace: true });
        }
        
      } catch (error) {
        console.error("❌ useInvitationCheck: Erreur inattendue", error);
        
        // ✅ Messages d'erreur spécifiques selon le code
        const errorData = (error as any)?.data || {};
        const errorCode = errorData.code || (error as any)?.code;
        
        let title = "Erreur";
        let description = "Impossible d'accepter l'invitation.";
        
        switch (errorCode) {
          case "INVITATION_NOT_FOUND":
            title = "Invitation introuvable";
            description = "Cette invitation n'existe pas ou a déjà été utilisée. Demandez une nouvelle invitation.";
            break;
          case "INVITATION_EXPIRED":
            title = "Invitation expirée";
            description = "Cette invitation a expiré. Demandez une nouvelle invitation.";
            break;
          case "EMAIL_MISMATCH":
            title = "Email incorrect";
            description = "Cette invitation n'est pas pour votre adresse email.";
            break;
          case "UNAUTHENTICATED":
            title = "Non authentifié";
            description = "Vous devez être connecté pour accepter une invitation.";
            break;
          default:
            description = "Impossible d'accepter l'invitation. Si le problème persiste, demandez une nouvelle invitation.";
        }
        
        toast({
          title,
          description,
          variant: "destructive",
        });
      } finally {
        // ✅ TOUJOURS nettoyer l'URL (stabilité > UX premium)
        // ✅ Créer une copie pour éviter mutation directe
        console.log("🧹 useInvitationCheck: Nettoyage URL");
        const next = new URLSearchParams(searchParams);
        next.delete("invite_token");
        next.delete("token");
        setSearchParams(next, { replace: true });
        
        setChecking(false);
      }
    };
    
    acceptInvitation();
  }, [authLoading, user, searchParams, setSearchParams, location.pathname, navigate, refreshProfile]);
  
  return { checking };
}
